// ============================================================
// FILE: src/pages/Dashboard/Dashboard.jsx
// PURPOSE: Main dashboard with stats, recent visits, alerts
// ============================================================

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../../api/services';
import { useAuth } from '../../context/AuthContext';
import '../../components/UI/UI.css';
import './Dashboard.css';

// ── Helper: status badge ──────────────────────────────────
const VisitBadge = ({ status }) => {
  const map = {
    'Completed':   'badge-success',
    'In Progress': 'badge-warning',
    'Scheduled':   'badge-info',
    'Cancelled':   'badge-danger',
  };
  return (
    <span className={`badge ${map[status] || 'badge-gray'}`}>
      {status}
    </span>
  );
};

// ── Format date helper ────────────────────────────────────
const fmt = (dt) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const fmtTime = (dt) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit'
  });
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await dashboardAPI.getStats();
        setData(res.data.data);
      } catch (e) {
        console.error('Dashboard load error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="loading-state">
        <span>⏳</span> Loading dashboard...
      </div>
    );
  }

  const s = data?.stats || {};

  return (
    <div className="dashboard">

      {/* ── Stat Cards ── */}
      <div className="stats-grid-4">
        <StatCard
          label="Total Clients"
          value={s.totalClients ?? 0}
          icon="👥"
          iconBg="#eff6ff"
          change="Active care recipients"
          onClick={() => navigate('/clients')}
        />
        <StatCard
          label="Active Caregivers"
          value={s.activeCaregivers ?? 0}
          icon="👨‍⚕️"
          iconBg="#f0fdf4"
          change="On your care team"
          onClick={() => navigate('/caregivers')}
        />
        <StatCard
          label="Today's Visits"
          value={s.todaysVisits ?? 0}
          icon="📅"
          iconBg="#fffbeb"
          change={`${s.completedToday ?? 0} completed · ${s.scheduledToday ?? 0} scheduled`}
          onClick={() => navigate('/visits')}
        />
        <StatCard
          label="Pending Billing"
          value={`$${Number(s.pendingBillingAmount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}`}
          icon="💰"
          iconBg="#fef2f2"
          change={`${s.pendingInvoiceCount ?? 0} invoices pending`}
          onClick={() => navigate('/billing')}
        />
      </div>

      {/* ── Main Grid ── */}
      <div className="dashboard-grid">

        {/* Left Column */}
        <div className="dashboard-col-main">

          {/* Recent Visits */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Recent Visits</span>
              <button className="card-action" onClick={() => navigate('/visits')}>
                View All →
              </button>
            </div>
            <div className="table-wrap">
              <table className="ec-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Caregiver</th>
                    <th>Service</th>
                    <th>Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.recentVisits?.length > 0 ? (
                    data.recentVisits.map((v) => (
                      <tr key={v.ID}>
                        <td className="bold">{v.ClientName}</td>
                        <td>{v.CaregiverName}</td>
                        <td>{v.ServiceName || '—'}</td>
                        <td style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
                          {fmtTime(v.StartTime)}
                          {v.EndTime && ` – ${fmtTime(v.EndTime)}`}
                        </td>
                        <td><VisitBadge status={v.VisitStatus} /></td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '30px' }}>
                        No visits yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Expiring Training */}
          {data?.expiringTraining?.length > 0 && (
            <div className="card" style={{ marginTop: 18 }}>
              <div className="card-header">
                <span className="card-title">⚠️ Training Expiring Soon</span>
                <button className="card-action" onClick={() => navigate('/training')}>
                  View All →
                </button>
              </div>
              <div className="table-wrap">
                <table className="ec-table">
                  <thead>
                    <tr>
                      <th>Caregiver</th>
                      <th>Training</th>
                      <th>Expires</th>
                      <th>Days Left</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.expiringTraining.map((t, i) => (
                      <tr key={i}>
                        <td className="bold">{t.CaregiverName}</td>
                        <td>{t.TrainingTitle}</td>
                        <td style={{ fontSize: '0.8rem' }}>
                          {new Date(t.ExpirationDate).toLocaleDateString()}
                        </td>
                        <td>
                          <span className={`badge ${t.DaysLeft <= 7 ? 'badge-danger' : 'badge-warning'}`}>
                            {t.DaysLeft}d left
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="dashboard-col-side">

          {/* Quick Actions */}
          <div className="card">
            <div className="card-title" style={{ marginBottom: 14 }}>Quick Actions</div>
            <div className="quick-actions">
              {[
                { icon: '➕', label: 'New Client',     path: '/clients' },
                { icon: '📅', label: 'Schedule Visit', path: '/visits' },
                { icon: '💰', label: 'Create Invoice', path: '/billing' },
                { icon: '📋', label: 'Care Plan',      path: '/careplans' },
                { icon: '⏰', label: 'Attendance',     path: '/attendance' },
                { icon: '📄', label: 'Documents',      path: '/documents' },
              ].map((a) => (
                <button
                  key={a.label}
                  className="quick-action-btn"
                  onClick={() => navigate(a.path)}
                >
                  <span className="quick-action-icon">{a.icon}</span>
                  <span className="quick-action-label">{a.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Notifications */}
          <div className="card" style={{ marginTop: 18 }}>
            <div className="card-header">
              <span className="card-title">🔔 Alerts</span>
              <button className="card-action" onClick={() => navigate('/notifications')}>
                View All
              </button>
            </div>
            {data?.notifications?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {data.notifications.map((n) => (
                  <AlertItem key={n.ID} notification={n} />
                ))}
              </div>
            ) : (
              <div className="empty-state" style={{ padding: '20px' }}>
                <div style={{ fontSize: '1.5rem' }}>✅</div>
                <div className="empty-state-text" style={{ marginTop: 6 }}>
                  No pending alerts
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────

function StatCard({ label, value, icon, iconBg, change, onClick }) {
  return (
    <div className="stat-card" onClick={onClick} style={{ cursor: 'pointer' }}>
      <div className="stat-card-header">
        <span className="stat-card-label">{label}</span>
        <div className="stat-card-icon" style={{ background: iconBg }}>
          {icon}
        </div>
      </div>
      <div className="stat-card-value">{value}</div>
      <div className="stat-card-change">{change}</div>
    </div>
  );
}

function AlertItem({ notification }) {
  const typeStyles = {
    MedicaidAlert:       { bg: '#fef2f2', border: '#ef4444', text: '#991b1b' },
    TrainingAlert:       { bg: '#fffbeb', border: '#f59e0b', text: '#92400e' },
    ProfileUpdateRequest:{ bg: '#eff6ff', border: '#3b82f6', text: '#1e40af' },
  };

  const style = typeStyles[notification.NotificationType] || {
    bg: '#f8fafc', border: '#94a3b8', text: '#334155'
  };

  return (
    <div style={{
      background: style.bg,
      borderLeft: `3px solid ${style.border}`,
      borderRadius: '0 8px 8px 0',
      padding: '10px 14px',
    }}>
      <div style={{
        fontSize: '0.82rem',
        fontWeight: 600,
        color: style.text,
        marginBottom: 3,
      }}>
        {notification.NotificationType?.replace(/([A-Z])/g, ' $1').trim()}
      </div>
      <div style={{ fontSize: '0.78rem', color: style.text, opacity: 0.85 }}>
        {notification.Message}
      </div>
    </div>
  );
}